import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Check, Calendar, MapPin, User, Building2, Globe, Monitor, Shield, Clock, Download, FileCheck } from 'lucide-react';
import { quotesApi } from '../services/api';
import { useToast } from '../hooks/use-toast';

const QuoteAcceptanceDetails = ({ quote, open, onClose }) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const { toast } = useToast();
  
  if (!quote || !quote.acceptance) {
    return null;
  }

  const acceptance = quote.acceptance;

  const handleDownloadAudit = async () => {
    setIsGenerating(true);
    try {
      // Generate acceptance audit PDF
      await quotesApi.generateAcceptanceAudit(quote.id);

      // Download MIT Auth-Header (Blob) — ein direkter Link laeuft in 403,
      // weil der JWT im localStorage liegt und nicht mitgesendet wird
      const res = await quotesApi.fetchAcceptanceAudit(quote.id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Annahme-Audit-${quote.quoteNumber || quote.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast({
        title: "Annahme-Audit PDF generiert ✅",
        description: "Das rechtssichere Annahme-Audit wurde erfolgreich erstellt.",
      });
    } catch (error) {
      toast({
        title: "Fehler",
        description: error?.response?.data?.detail || "Annahme-Audit PDF konnte nicht erstellt werden.",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <Check className="w-6 h-6 text-green-600" />
            Angebot rechtssicher angenommen
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          
          {/* Status Badge */}
          <div className="flex flex-col items-center gap-4">
            <Badge className="bg-green-600 text-white text-lg px-6 py-2">
              ✅ Angenommen am {new Date(acceptance.acceptedAt).toLocaleString('de-DE')}
            </Badge>
            
            {/* Export Audit PDF Button */}
            <Button
              onClick={handleDownloadAudit}
              disabled={isGenerating}
              className="flex items-center gap-2 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white shadow-lg"
            >
              {isGenerating ? (
                <>
                  <Clock className="w-4 h-4 animate-spin" />
                  Generiere PDF...
                </>
              ) : (
                <>
                  <FileCheck className="w-4 h-4" />
                  Rechtssicheres Annahme-Audit exportieren
                </>
              )}
            </Button>
            <p className="text-xs text-slate-600 text-center max-w-2xl">
              Das Audit-PDF enthält das komplette Angebot, die digitale Unterschrift und alle rechtssicheren Verifizierungsdaten 
              (Zeitstempel, IP-Adresse, Browser-Info, Security-Token) für Ihre Unterlagen.
            </p>
          </div>

          {/* Unterschrift */}
          {acceptance.digitalSignature && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                  Digitale Unterschrift
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="border-2 border-slate-200 rounded-lg p-4 bg-slate-50">
                  <img 
                    src={acceptance.digitalSignature} 
                    alt="Digitale Unterschrift" 
                    className="max-w-full h-auto"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  Die Unterschrift wurde als verschlüsseltes PNG-Bild gespeichert und ist rechtlich bindend.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Unterzeichner-Daten */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Unterzeichner
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <User className="w-5 h-5 text-slate-500 mt-1" />
                  <div>
                    <p className="text-sm font-semibold text-slate-700">Name</p>
                    <p className="text-base">{acceptance.signerName}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Building2 className="w-5 h-5 text-slate-500 mt-1" />
                  <div>
                    <p className="text-sm font-semibold text-slate-700">Firma</p>
                    <p className="text-base">{acceptance.signerCompany}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-slate-500 mt-1" />
                  <div>
                    <p className="text-sm font-semibold text-slate-700">Ort</p>
                    <p className="text-base">{acceptance.signerLocation}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-slate-500 mt-1" />
                  <div>
                    <p className="text-sm font-semibold text-slate-700">Datum (angegeben)</p>
                    <p className="text-base">{acceptance.signatureDate}</p>
                  </div>
                </div>
              </div>

              {acceptance.customerNotes && (
                <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm font-semibold text-slate-700 mb-2">Anmerkungen des Kunden:</p>
                  <p className="text-sm text-slate-600 whitespace-pre-wrap">{acceptance.customerNotes}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Rechtssichere Daten */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-600" />
                Rechtssichere Verifizierung
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                
                {/* Timestamp */}
                <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                  <Clock className="w-5 h-5 text-blue-600 mt-1" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-blue-900">Exakter Zeitstempel</p>
                    <p className="text-sm text-blue-700">
                      {new Date(acceptance.timestamp || acceptance.acceptedAt).toLocaleString('de-DE', {
                        dateStyle: 'full',
                        timeStyle: 'long'
                      })}
                    </p>
                    <p className="text-xs text-blue-600 mt-1">
                      UTC: {new Date(acceptance.timestamp || acceptance.acceptedAt).toISOString()}
                    </p>
                  </div>
                </div>

                {/* IP-Adresse */}
                {acceptance.ipAddress && (
                  <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                    <Globe className="w-5 h-5 text-slate-600 mt-1" />
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-slate-700">IP-Adresse</p>
                      <p className="text-sm text-slate-600 font-mono">{acceptance.ipAddress}</p>
                      <p className="text-xs text-slate-500 mt-1">
                        Die IP-Adresse dokumentiert den geografischen Ursprung der Annahme.
                      </p>
                    </div>
                  </div>
                )}

                {/* User-Agent */}
                {acceptance.userAgent && (
                  <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                    <Monitor className="w-5 h-5 text-slate-600 mt-1" />
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-slate-700">Browser-Information</p>
                      <p className="text-xs text-slate-600 font-mono break-all">{acceptance.userAgent}</p>
                      <p className="text-xs text-slate-500 mt-1">
                        Der User-Agent identifiziert das verwendete Gerät und den Browser.
                      </p>
                    </div>
                  </div>
                )}

                {/* Security Token */}
                {acceptance.securityToken && (
                  <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
                    <Shield className="w-5 h-5 text-green-600 mt-1" />
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-green-900">Eindeutiger Sicherheits-Token</p>
                      <p className="text-xs text-green-700 font-mono break-all">{acceptance.securityToken}</p>
                      <p className="text-xs text-green-600 mt-1">
                        Dieser Token dient zur eindeutigen Verifizierung und kann nicht gefälscht werden.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Optionale Positionen — ALLE anzeigen, mit Status.
              Vorher wurden nur die angenommenen gelistet und die Karte blieb
              ganz aus, wenn der Kunde alle Optionen abgelehnt hatte. Dann war
              nicht unterscheidbar, ob es keine Optionen gab oder keine
              gewaehlt wurde. Das Audit-PDF macht es laengst richtig. */}
          {(() => {
            const alleOptionen = (quote?.selectedServices || []).filter((sv) => sv?.isOptional);
            if (alleOptionen.length === 0) return null;

            const auswahl = acceptance.optionalSelections || {};
            const istGewaehlt = (sv) => !!(auswahl[sv.serviceId] || auswahl[sv.id]);
            const fmtEur = (n) => (Number(n) || 0).toLocaleString('de-DE', {
              minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
            const suffix = (ty) => ty === 'monatlich' ? ' / Monat'
              : (ty === 'jaehrlich' || ty === 'jährlich') ? ' / Jahr' : '';
            const zeile = (sv) => (Number(sv.price) || 0) * (Number(sv.quantity) || 1);

            const gewaehlt = alleOptionen.filter(istGewaehlt);
            const summen = gewaehlt.reduce((acc, sv) => {
              const k = sv.type === 'monatlich' ? 'monatlich'
                : (sv.type === 'jaehrlich' || sv.type === 'jährlich') ? 'jaehrlich' : 'einmalig';
              acc[k] += zeile(sv);
              return acc;
            }, { einmalig: 0, monatlich: 0, jaehrlich: 0 });

            return (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Check className="w-5 h-5" />
                    Optionale Positionen
                    <span className="ml-auto text-sm font-normal text-slate-500">
                      {gewaehlt.length} von {alleOptionen.length} beauftragt
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {alleOptionen.map((sv, i) => {
                      const ja = istGewaehlt(sv);
                      return (
                        <div
                          key={sv.serviceId || sv.id || i}
                          className={`flex items-center gap-2 p-2 rounded ${
                            ja ? 'bg-emerald-50' : 'bg-slate-50 opacity-70'}`}
                        >
                          {ja
                            ? <Check className="w-4 h-4 text-emerald-700 flex-shrink-0" />
                            : <span className="w-4 h-4 flex-shrink-0 text-slate-400 text-center leading-4">—</span>}
                          <span className={`text-sm ${ja ? 'text-emerald-900' : 'text-slate-500 line-through'}`}>
                            {sv.name || 'Position'}
                          </span>
                          <span className={`ml-auto text-sm whitespace-nowrap ${
                            ja ? 'font-medium text-emerald-900' : 'text-slate-400'}`}>
                            {fmtEur(zeile(sv))}{suffix(sv.type)}
                          </span>
                        </div>
                      );
                    })}
                    <div className="mt-3 pt-3 border-t border-slate-200 text-sm text-slate-700">
                      <span className="font-medium">Zusätzlich beauftragt: </span>
                      {[
                        summen.einmalig > 0 ? `${fmtEur(summen.einmalig)} einmalig` : null,
                        summen.monatlich > 0 ? `${fmtEur(summen.monatlich)} / Monat` : null,
                        summen.jaehrlich > 0 ? `${fmtEur(summen.jaehrlich)} / Jahr` : null,
                      ].filter(Boolean).join(' · ') || 'keine Option beauftragt'}
                      <span className="text-slate-400"> (netto)</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })()}

          {/* DSGVO Hinweis */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-900">
              <strong>🔒 DSGVO-konform:</strong> Alle Daten werden gemäß EU-Datenschutz-Grundverordnung gespeichert 
              und verarbeitet. Die digitale Unterschrift und Verifizierungsdaten sind rechtlich bindend und können 
              bei Bedarf als Nachweis verwendet werden.
            </p>
          </div>

        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QuoteAcceptanceDetails;
